import {
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ListItemIcon,
  ListItemText,
  Typography
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import ConfirmationDialog from 'src/components/ConfirmationDialog';
import { PostUtil } from 'src/pages/new-post-new/post-util';
import HttpServices from 'src/services/httpService';
import { crmModuleURL } from 'src/services/urlService';
import { CommonFunction } from 'src/utils/commonFunctions/common-function.component';
import { useToaster } from 'src/utils/toaster/toasterContext';
import { useTranslation } from 'react-i18next';

const AddCRMAccount = ({ handleClose, handleSuccess }) => {
  const { t: translate } = useTranslation();
  const [crmAccounts, setCrmAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [loader, setLoader] = useState(false); // Added loader state
  const theme = useTheme();
  const { showToast } = useToaster();

  useEffect(() => {
    getNotCrmAccounts();
  }, []);

  const getNotCrmAccounts = () => {
    setLoader(true);
    HttpServices.Get({
      url: crmModuleURL.getNotCrmAccounts,
      successCallback: (data, message) => {
        showToast(message, 's');
        const filteredAccounts = data?.filter(
          (acc) =>
            acc.is_enabled  &&
            ((acc.service_name.toUpperCase() === PostUtil.ServiceName.FACEBOOK &&
              acc.profile_type === PostUtil.ProfileType.PAGE) ||
              acc.service_name.toUpperCase() === PostUtil.ServiceName.INSTAGRAM)
        );
        setCrmAccounts(filteredAccounts);
        setLoader(false);
      },
      failureCallback: (message) => {
        showToast(message, 'e');
        setLoader(false);
      },
    });
  };

  const handleAddClick = (account) => {
    setSelectedAccount(account);
    setConfirmOpen(true);
  };

  const confirmAddSubscription = () => {
    HttpServices.Post({
      url: crmModuleURL.subscribeWebhookEvent,
      body: { connected_profile_id: selectedAccount.id, service_name: selectedAccount?.service_name },
      successCallback: (data, message) => {
        showToast(message, 's');
        setConfirmOpen(false);
        handleSuccess?.();
      },
      failureCallback: (message) => {
        showToast(message, 'e');
        setConfirmOpen(false);
      },
    });
  };
  const handleCloseConfirm = () => {
    setConfirmOpen(false);
  };
  return (
    <Dialog open onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h4" gutterBottom>
          {translate('crm.add_webhook_subscription_accounts', 'Add Webhook Subscription Accounts')}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
      <Card
        variant="outlined"
        sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}
      >
        {loader ? (
          <CircularProgress sx={{ color: theme.palette.primary.main }} />
        ) : crmAccounts?.length > 0 ? (
          <Box sx={{ width: '100%' }}>
            {crmAccounts.map((profile) => (
              <Box
                key={profile.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  p: 1,
                  '&:not(:last-child)': {
                    borderBottom: `1px solid ${theme.palette.divider}`,
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Badge
                    overlap="circular"
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    badgeContent={CommonFunction.SocialMediaAccountImage(profile.service_name)}
                  >
                    <Avatar
                      src={profile.profile_image}
                      sx={{
                        width: 40,
                        height: 40,
                        bgcolor: theme.palette.primary.main,
                      }}
                    >
                      {!profile.profile_image && profile.profile_name?.charAt(0).toUpperCase()}
                    </Avatar>
                  </Badge>
                </ListItemIcon>

                <ListItemText
                  primary={
                    <Typography variant="subtitle2" noWrap>
                      {profile.profile_name}
                    </Typography>
                  }
                  secondary={
                    <Typography variant="caption" color="text.secondary">
                      {profile.service_name?.toUpperCase()} • {profile.profile_type}
                    </Typography>
                  }
                  sx={{ ml: 1, flexGrow: 1 }}
                />

                <Button
                  variant="contained"
                  size="small"
                  onClick={() => handleAddClick(profile)}
                  disabled={loader}
                >
                  {translate('crm.add_subscription', 'Add Subscription')}
                </Button>
              </Box>
            ))}
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              p: 2,
              '&:not(:last-child)': {
                borderBottom: `1px solid ${theme.palette.divider}`,
              },
            }}
          >
            <Typography variant="body1" color="text.secondary">
              {translate('crm.no_connected_profiles', 'No connected profiles available')}
            </Typography>
          </Box>
        )}
      </Card>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} variant="outlined" color="error">
          {translate('common.cancel', 'Cancel')}
        </Button>
      </DialogActions>

      <ConfirmationDialog
        open={confirmOpen}
        onClose={handleCloseConfirm}
        onConfirm={confirmAddSubscription}
        title={translate('crm.add_subscription', 'Add Subscription')}
        message={translate('crm.confirm_add_subscription', 'Are you sure you want to add a subscription?')}
      />
    </Dialog>
  );
};

export default AddCRMAccount;
